import { ApolloQueryResult } from '@apollo/client';
import { LoadingButton } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import AddIcon from '@atlaskit/icon/glyph/add';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { R300, R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { BillingAdminPerson, DynamicResultTable, OpenSidebarButton, useUpdateTransactionAccount } from '@css-xp/base';
import { generatedTypesNew, Maybe } from '@css-xp/graphtypes';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { emptyStateProp } from '@atlassian/cssxp-utils-entitlement';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import './customer-contact-tab.scss';

interface CustomerContactsTabProps {
  entitlementId: generatedTypesNew.EntitlementDetails['id'];
  transactionAccountId: string;
  endCustomerContacts: generatedTypesNew.EndCustomerContact[];
  totalCount: number;
  isLoading: boolean;
  error?: Error;
  addCustomerContactSidebarType: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface CheckBoxStateType {
  email: Maybe<string>;
  isChecked: boolean;
}

const DEFAULT_ROWS_PER_PAGE = 8;

const getDataForCurrentPage = (currentPage: number, pageSize: number, data: generatedTypesNew.EndCustomerContact[]) => {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
};

const CustomerContacts = ({
  endCustomerContacts,
  totalCount,
  isLoading,
  error,
  addCustomerContactSidebarType,
  entitlementId,
  transactionAccountId,
  refetch,
}: CustomerContactsTabProps) => {
  const [contactsCheckboxState, setContactsCheckBoxState] = useState<CheckBoxStateType[]>([]);
  const [isRemoveButtonDisabled, setIsRemoveButtonDisabled] = useState(true);
  const [emailBeingRemoved, setEmailBeingRemoved] = useState<Maybe<string>[]>([]);
  const [tableViewData, setTableViewData] = useState<{
    data: generatedTypesNew.EndCustomerContact[];
    currentPage: number;
    rowsPerPage: number;
  }>({
    data: [],
    currentPage: 1,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });
  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({
    // eslint-disable-next-line
    onClose: () => {
      //do nothing
    },
    onComplete: () => {
      refetch();
    },
    sendNotification: true,
    notification: {
      failureHeading: 'Update customer contact failed',
      failureMessage: 'Error while removing customer contacts',
      successHeading: 'Update customer contact succeeded',
      successMessage: 'Successfully removed customer contacts.',
    },
  });

  const isAllContactsSelected = contactsCheckboxState.filter((el) => el.isChecked).length === totalCount;
  const isContactSelected = !!contactsCheckboxState.find((el) => el.isChecked);

  useEffect(() => {
    if (!isLoading && !error) {
      setTableViewData({
        ...tableViewData,
        data: getDataForCurrentPage(1, tableViewData.rowsPerPage, endCustomerContacts),
      });
      setContactsCheckBoxState(
        endCustomerContacts.map((user) => ({
          email: user.email,
          isChecked: false,
        }))
      );
    }
  }, [isLoading]);

  const getErrorState = useCallback((error: string | undefined) => {
    const renderImage = () => <WarningIcon label="Error" size="xlarge" primaryColor={R300} />;
    return {
      header: '',
      description: (
        <span className="error-view">Oh no! Something went wrong, please try again. {error && error.toString()}</span>
      ),
      renderImage,
    };
  }, []);

  const handleCheckboxClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const clonedIsCheckedArray = [...contactsCheckboxState];
      const clonedEmailBeingRemoved = [...emailBeingRemoved];
      const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
      if (event.target.checked) {
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: true };
        setEmailBeingRemoved([...emailBeingRemoved, value]);
      } else {
        clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: false };
        clonedEmailBeingRemoved.splice(clonedEmailBeingRemoved.indexOf(value), 1);
        setEmailBeingRemoved(clonedEmailBeingRemoved);
      }
      setIsRemoveButtonDisabled(false);
      setContactsCheckBoxState(clonedIsCheckedArray);
    },
    [contactsCheckboxState, emailBeingRemoved]
  );

  const handlePageChange = useCallback(
    (pageNo: number) => {
      setTableViewData({
        ...tableViewData,
        currentPage: pageNo,
        data: getDataForCurrentPage(pageNo, tableViewData.rowsPerPage, endCustomerContacts),
      });
    },
    [tableViewData, endCustomerContacts]
  );

  const handleRowSizeChange = useCallback(
    (rowSize: number) => {
      setTableViewData({
        currentPage: 1,
        rowsPerPage: rowSize,
        data: getDataForCurrentPage(1, rowSize, endCustomerContacts),
      });
    },
    [endCustomerContacts]
  );

  const handleRemoveClick = useCallback(() => {
    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      entitlementId,
      modifiedCustomerContacts: {
        emailsOrAaidsToDelete: emailBeingRemoved as string[],
      },
    };
    updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
  }, [emailBeingRemoved, entitlementId, transactionAccountId, updateTransactionAccount]);

  const renderRemoveButton = useCallback(
    () => (
      <div className="remove-contact-btn-container">
        <Tooltip content="Select a customer contact first" position="top">
          <LoadingButton
            testId="remove-contact-btn"
            appearance="primary"
            isLoading={isProcessing}
            onClick={() => handleRemoveClick()}
            isDisabled={isRemoveButtonDisabled || !isContactSelected || isAllContactsSelected}
          >
            Remove
          </LoadingButton>
        </Tooltip>
      </div>
    ),
    [isProcessing, isRemoveButtonDisabled, isContactSelected, isAllContactsSelected, handleRemoveClick]
  );

  const renderActionsOnTop = useCallback(() => {
    return [
      () => (
        <div className="customer-contact-actions-add-container">
          <div className="customer-contact-actions add">
            <div className={`add-action add-action-link`}>
              <OpenSidebarButton
                sidebar={addCustomerContactSidebarType}
                payload={{
                  entitlementId,
                  txaId: transactionAccountId,
                  refetch,
                }}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                //@ts-ignore
                iconBefore={<AddIcon label="Add icon" size="small" />}
                appearance="link"
                testId="add-customer-contact"
              >
                Add a customer contact
              </OpenSidebarButton>
            </div>
            <span className="add-subtext">An active partner account requires at least one customer contact.</span>
          </div>
          <div className="remove-contact-btn-container">
            {!isLoading && !error && isContactSelected && isAllContactsSelected && (
              <div className="remove-warning-message">
                {/* @ts-ignore */}
                <ErrorIcon size="small" label="error" primaryColor={R400} />
                <p className="remove-contact-warning">There must be at least one customer contact remaining</p>
              </div>
            )}
            {renderRemoveButton()}
          </div>
        </div>
      ),
    ];
  }, [
    addCustomerContactSidebarType,
    entitlementId,
    transactionAccountId,
    refetch,
    isLoading,
    error,
    isContactSelected,
    isAllContactsSelected,
    renderRemoveButton,
  ]);

  const makeColumns = useCallback(() => {
    return tableViewData.data.map((user: generatedTypesNew.EndCustomerContact, idx: number) => {
      const rowKey = `customer-contact-row-${idx}`;
      return {
        key: rowKey,
        cells: [
          {
            content: (
              <div className="bulk-contact-removal-container">
                <Checkbox
                  testId="contact-checkbox"
                  isChecked={
                    contactsCheckboxState[contactsCheckboxState.findIndex((el) => el.email === user.email)]?.isChecked
                  }
                  onChange={handleCheckboxClick}
                  value={user.email as string}
                  name={`${rowKey}-remove-contact`}
                  className="remove-bulk-contact-checkbox"
                />
                <BillingAdminPerson fullName={user.name} email={user.email} picture={user.picture ?? ''} />
              </div>
            ),
          },
        ],
      };
    });
  }, [contactsCheckboxState, handleCheckboxClick, tableViewData]);

  return (
    <div className="customer-contact-container billing-admins-container">
      <div className="customer-contact-table">
        <DynamicResultTable
          isLoading={isLoading}
          testId="customer-contact"
          headingText="Customer contacts"
          className="customer-contact-table"
          head={{ cells: [] }}
          rows={makeColumns()}
          emptyStateProps={emptyStateProp}
          totalCount={totalCount}
          currentPage={tableViewData.currentPage}
          rowsPerPage={tableViewData.rowsPerPage}
          onPageChange={handlePageChange}
          isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
          onRowChange={handleRowSizeChange}
          actions={renderActionsOnTop()}
          error={error?.toString()}
          errorStateProps={getErrorState(error?.message)}
        />
      </div>
    </div>
  );
};

export default CustomerContacts;
